import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Table, Button, Input, Dropdown, Tag, Image, message, Modal, MenuProps } from "antd";
import {
    PlusOutlined,
    EyeOutlined,
    EditOutlined,
    DeleteOutlined,
    MoreOutlined,
} from "@ant-design/icons";
import CategoryModal from "./AddModal";
import Layout from '../../Layout'
import useAuthStore from "../../store/authStore";
import useTableColumnSettings, { SettingColumn } from "../../hooks/useTableColumnSettings";
import { ColumnSettingButton } from "../../components/TableColumnSetting";

const { Search } = Input;

interface Category {
    id: number;
    name: string;
    description: string;
    status: string;
    image: string;
    updatedAt: string;
}

const mockCategories = [
    {
        id: 1,
        name: "Làm đẹp",
        description: "Các sản phẩm chăm sóc sắc đẹp",
        image: "https://picsum.photos/200?1",
        status: "1",
        updatedAt: "2026-03-02 13:49",
    },
    {
        id: 2,
        name: "Điện thoại",
        description: "Smartphone và phụ kiện",
        image: "https://picsum.photos/200?2",
        status: "1",
        updatedAt: "2026-03-01 10:15",
    },
    {
        id: 3,
        name: "Thời trang",
        description: "Quần áo nam nữ",
        image: "https://picsum.photos/200?3",
        status: "0",
        updatedAt: "2026-02-28 09:22",
    },
    {
        id: 4,
        name: "Mỹ phẩm",
        description: "Trang điểm và skincare",
        image: "https://picsum.photos/200?4",
        status: "1",
        updatedAt: "2026-02-27 15:40",
    },
    {
        id: 5,
        name: "Laptop",
        description: "Laptop và phụ kiện máy tính",
        image: "https://picsum.photos/200?5",
        status: "1",
        updatedAt: "2026-02-26 18:12",
    },
    {
        id: 6,
        name: "Gia dụng",
        description: "Đồ dùng trong nhà",
        image: "https://picsum.photos/200?6",
        status: "0",
        updatedAt: "2026-02-25 11:03",
    },
    {
        id: 7,
        name: "Thể thao",
        description: "Dụng cụ tập luyện",
        image: "https://picsum.photos/200?7",
        status: "1",
        updatedAt: "2026-02-24 14:55",
    },
    {
        id: 8,
        name: "Đồ chơi",
        description: "Đồ chơi trẻ em",
        image: "https://picsum.photos/200?8",
        status: "1",
        updatedAt: "2026-02-23 08:30",
    },
];

export default function CategoryPage() {
  const { t } = useTranslation();
    // Phan quyen thao tac: chi co quyen xem thi khong hien nut them/sua/xoa
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canCreate = isSuperAdmin || hasPermission("product_categories.create");
    const canEdit = isSuperAdmin || hasPermission("product_categories.edit");
    const canDelete = isSuperAdmin || hasPermission("product_categories.delete");
    const [openModal, setOpenModal] = useState(false);

    const [categories, setCategories] = useState(mockCategories);
    const [editingCategory, setEditingCategory] = useState<Category | null>(null);

    const handleAddCategory = (data: any) => {
        const newCategory: Category = {
            id: Date.now(),
            name: data.name,
            description: data.description,
            status: data.status,
            image: data.image,
            updatedAt: new Date().toLocaleString(),
        };

        setCategories((prev) => [newCategory, ...prev]);
    };

    const handleEdit = (record: any) => {
        setEditingCategory(record);
        setOpenModal(true);
    };

    const handleDelete = (id: number) => {
        Modal.confirm({
            title: t('products_cat.delete_title'),
            content: t('products_cat.delete_confirm'),
            okText: t('products_cat.delete'),
            okType: "danger",
            cancelText: t('products_cat.cancel'),

            onOk() {
                setCategories((prev) => prev.filter((item) => item.id !== id));
                message.success(t('products_cat.deleted'));
            },
        });
    };

    const columns: SettingColumn<Category>[] = [
        {
            title: t('products_cat.col_name'),
            dataIndex: "name",
            render: (text: string) => (
                <span className="text-[#3e91ab] font-medium">{text}</span>
            ),
        },
        {
            title: t('products_cat.col_description'),
            dataIndex: "description",
        },
        {
            title: t('products_cat.col_image'),
            dataIndex: "image",
            render: (img: string) =>
                img ? (
                    <Image
                        src={img}
                        width={40}
                        height={40}
                        className="object-cover rounded"
                    />
                ) : (
                    "-"
                ),
        },
        {
            title: t('products_cat.col_status'),
            dataIndex: "status",
            render: (status: string) =>
                status === "1" ? (
                    <Tag color="green">{t('products_cat.status_active')}</Tag>
                ) : (
                    <Tag color="red">{t('products_cat.status_inactive')}</Tag>
                ),
        },
        {
            title: t('products_cat.col_updated_at'),
            dataIndex: "updatedAt",
        },
        {
            key: "actions",
            title: t('products_cat.col_action'),
            fixed: "right",
            lockVisible: true,
            render: (_: any, record?: Category) => {

                const items: MenuProps["items"] = [
                    ...(canEdit ? [{
                        key: "edit",
                        icon: <EditOutlined />,
                        label: t('products_cat.edit'),
                        onClick: () => handleEdit(record),
                    }] : []),
                    ...(canDelete ? [{
                        key: "delete",
                        icon: <DeleteOutlined />,
                        label: t('products_cat.delete'),
                        danger: true,
                        onClick: () => record?.id && handleDelete(record.id),
                    }] : []),
                ];

                return (
                    <Dropdown menu={{ items }} trigger={["click"]}>
                        <Button icon={<MoreOutlined />} />
                    </Dropdown>
                );
            },
        },
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings(
        "product_category_list",
        (canEdit || canDelete) ? columns : columns.filter((c) => c.key !== "actions")
    );

    const handleSave = (values: any) => {

        if (editingCategory) {
            setCategories((prev) =>
                prev.map((item) =>
                    item.id === editingCategory.id
                        ? { ...item, ...values }
                        : item
                )
            );

            message.success(t('products_cat.updated'));
        } else {
            setCategories((prev) => [
                {
                    id: Date.now(),
                    ...values,
                    updatedAt: new Date().toLocaleString(),
                },
                ...prev,
            ]);

            message.success(t('products_cat.created'));
        }

        setEditingCategory(null);
    };


    return (
        <Layout>
            <Helmet>
                <title>{t('page.product_categories')} | Khothi</title>
            </Helmet>
            <>
                <div className="flex gap-2 flex-col md:flex-row md:justify-between mb-4">
                    <h2 className="text-lg text-gray-900 font-semibold t-text-white">
                        {t('products_cat.title')}
                    </h2>

                    <div className="flex gap-2">
                        <Search placeholder={t('products_cat.search')} className="w-[220px]" />

                        {canCreate && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                className="bg-[#3e91ab] hover:!bg-[#357f96] border-[#3e91ab]"
                                onClick={() => setOpenModal(true)}
                            >
                                {t('products_cat.add')}
                            </Button>
                        )}

                        <ColumnSettingButton {...columnSettingProps} />
                    </div>
                </div>

                <Table
                    columns={tableColumns}
                    components={tableComponents}
                    tableLayout={tableLayout}
                    dataSource={categories}
                    rowKey="id"
                    className="custom-ant-table"
                    scroll={{ x: scrollX }}
                />

                <CategoryModal
                    open={openModal}
                    setOpen={setOpenModal}
                    onCreate={handleSave}
                    editingCategory={editingCategory}
                />

            </>
        </Layout >
    );
}